
import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

interface AnimatedTextProps {
  text: string;
  className?: string;
  animation?: 'fade' | 'slide' | 'typewriter' | 'gradient';
  delay?: number;
  duration?: number;
}

export const AnimatedText = ({
  text,
  className,
  animation = 'fade',
  delay = 0,
  duration = 0.8
}: AnimatedTextProps) => {
  // Debug log to verify component is being called
  console.log("AnimatedText rendering with text:", text, "animation:", animation);
  
  const words = text.split(' ');
  const letters = text.split('');

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: animation === 'typewriter' ? 0.03 : 0.1,
        delayChildren: delay
      }
    }
  };

  const fadeVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration }
    }
  };

  const slideVariants = {
    hidden: { opacity: 0, x: -30, rotateX: -90 },
    visible: {
      opacity: 1,
      x: 0,
      rotateX: 0,
      transition: { duration, ease: 'easeOut' }
    }
  };

  const typewriterVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.1 }
    }
  };

  const getVariants = () => {
    switch (animation) {
      case 'slide': return slideVariants;
      case 'typewriter': return typewriterVariants;
      default: return fadeVariants;
    }
  };

  // Função para verificar se uma palavra deve ter destaque
  const shouldHighlight = (word: string) => {
    const cleanWord = word.toLowerCase().replace(/[.,!?;]/g, '');
    return cleanWord === 'metrocalma' || cleanWord === 'inspire' || cleanWord === 'expire';
  };

  // Fallback simples se animações falharem
  const SimpleFallback = () => (
    <span className={cn(className, 'block')}>
      {text}
    </span>
  );

  if (animation === 'gradient') {
    try {
      return (
        <motion.span
          className={cn(
            'bg-gradient-to-r from-brand-primary via-brand-secondary to-brand-accent bg-clip-text text-transparent bg-[length:200%_100%] block',
            className
          )}
          initial={{ opacity: 0, backgroundPosition: '0% 50%' }}
          animate={{ 
            opacity: 1,
            backgroundPosition: ['0% 50%', '100% 50%', '0% 50%']
          }}
          transition={{ 
            opacity: { duration, delay },
            backgroundPosition: { 
              duration: 3, 
              repeat: Infinity, 
              ease: 'linear' 
            }
          }}
        >
          {text}
        </motion.span>
      );
    } catch (error) {
      console.error("AnimatedText gradient animation error:", error);
      return <SimpleFallback />;
    }
  }

  if (animation === 'typewriter') {
    try {
      return (
        <motion.span
          className={cn(className, 'block')}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {letters.map((letter, index) => (
            <motion.span
              key={index}
              variants={getVariants()}
              style={{ display: 'inline-block' }}
            >
              {letter === ' ' ? '\u00A0' : letter}
            </motion.span>
          ))}
        </motion.span>
      );
    } catch (error) {
      console.error("AnimatedText typewriter animation error:", error);
      return <SimpleFallback />;
    }
  }

  try {
    return (
      <motion.span
        className={cn(className, 'block')}
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {words.map((word, index) => (
          <motion.span
            key={index}
            variants={getVariants()}
            className={cn(
              'inline-block',
              shouldHighlight(word) && 'font-extrabold text-brand-primary drop-shadow-sm'
            )}
            style={{ 
              paddingRight: index < words.length - 1 ? '0.25em' : '0.5em'
            }}
          >
            {word}
          </motion.span>
        ))}
      </motion.span>
    );
  } catch (error) {
    console.error("AnimatedText animation error:", error);
    return <SimpleFallback />;
  }
};
